import { useLiveBondRefresh } from "../../features/assetCatalogue/useLiveBondRefresh";
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Switch,
  Route,
  useRouteMatch,
  useParams,
  useLocation,
} from 'react-router-dom';

import { Redirect } from 'reusable';
import { LoadingScreen, PausedSession } from 'components';
import {
  selectors,
  fetchBadges,
  connectToSessionSocket,
  disconnectFromSessionSocket,
  retrieveSession,
  fetchKlasses,
  fetchSessions,
  FINISHED,
  HISTORICAL_SCENARIO,
  REAL_TIME_SCENARIO,
  fetchHistoricalStep,
  fetchForwardContracts,
  fetchMarketHours,
  fetchCDSContracts,
  fetchRepoContracts,
} from 'state';
import PersonSelector from '../TraineeDashboard/PersonSelector';
import { IndividualReport } from 'reusable';
import Header from './Header';
import RiskProfile from './RiskProfile';
import MarketsTab from './MarketsTabV5';
import BlotterTabRoute from './BlotterTabRoute';
import OpenOrders from './OpenOrders';
import Metrics from './Metrics';
import Settings from './Settings';
import PortfolioTab from './PortfolioTab';
import NewsHistory from './NewsHistory';
import MacroBackground from './MacroBackground';
import Benchmarks from './Benchmarks';
import SoundPlayer from './SoundPlayer';
import CookieProvider from './CookieProvider';
import EquitiesResearch from './EquitiesResearch';
import Insights from './Insights';
import AnalysisStudioSelf from '../TrainerSession/AnalysisStudioLive/AnalysisStudioSelf';
import HistoricalProgressTrainee from '../../components/Charts/HistoricalProgressTrainee';
import { TraineeSessionContextWrapper } from './TraineeSessionContext';
import Auctions from './Auctions';
import { fetchAuctions, fetchYields } from '../../state/app/auctions/actions';
import { fetchClientMoney } from '../../state/app/client_money/actions';
import { TraineeOptionsContextWrapper } from './TraineeOptionsContext';
import OptionsIndex from './OptionsTab/OptionsIndex';
import {
  fetchOptionsChains,
  fetchOptionsChainsNew,
  fetchPlayerOptionsInstruments,
} from '../../state/app/options/actions';
import { optionChainFetched } from '../../state/app/selectors';
import PassingTimeBanner from '../../components/PassingTimeBanner';
import CentralBankPlayerTab from './MarketsTab/CentralBankPlayerTab';
import BustBanner from '../../components/BustBanner';
import NewsHistory2 from './NewsHistory2';
import { TraineeSimplifiedOptionOrderRefresh } from '../../reusable/SimplifiedOptionOrderRefresh';
import Assessment from './Assessment';
import { fetchAssessmentState } from '../../state/app/assessment/actions';

// TraineeSession actually
import { useMonitoringReady } from '../../components/MonitoringObserver';
import { setPresencePerson } from '../../services/presence';

const TraineeDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectors.token) as string;
  const session = useSelector(selectors.activeSession);
  const player = useSelector(selectors.player);
  const selectedPerson = useSelector(selectors.selectedPerson);
  const assets = useSelector(selectors.underlyingAssets);
  // const currentlyPassingTime = useSelector(
  //   selectors.currentlyPassingTimeSelector
  // );

  const { url } = useRouteMatch();
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  useEffect(() => {
    setPresencePerson(selectedPerson?.id, sessionId);
  }, [selectedPerson, sessionId]);
  useMonitoringReady(sessionId, session?.id === sessionId && !!player?.id, 'legacy');
  useLiveBondRefresh(sessionId);

  const location = useLocation();

  // secret backdoor flag – read once from initial URL
  const [bustBypass] = React.useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('bypass_please') === '34'; // <--- your secret param
  });

  useEffect(() => {
    // dispatch(retrievePlayerId({id}));
    dispatch(retrieveSession({ id: sessionId }));
    dispatch(fetchBadges({}));
    dispatch(fetchKlasses({}));
    dispatch(fetchSessions({})); // why do we fetch all sessions when entering a single session?
    dispatch(fetchHistoricalStep({}));
    dispatch(fetchAuctions({ session_id: sessionId }));
    // console.log('id: ', id);
    dispatch(fetchForwardContracts({ session_id: sessionId }));
    dispatch(fetchCDSContracts({ session_id: sessionId }));
    dispatch(fetchRepoContracts({ session_id: sessionId }));
    dispatch(fetchYields({ session_id: sessionId }));
    dispatch(fetchMarketHours({}));

    // dispatch(fetchOptionsChains({ session_id: session.id }));
    // dispatch(fetchOptionsInstruments({ player_id: player.id }));
    dispatch(fetchOptionsChainsNew({ session_id: sessionId }));
    dispatch(fetchPlayerOptionsInstruments({ session_id: sessionId }));

    dispatch(connectToSessionSocket(token, id));

    return () => {
      dispatch(disconnectFromSessionSocket(token, id));
    };
  }, [dispatch, id, sessionId, token]);

  // useEffect(() => {
  //   if (
  //     Object.keys(player).length !== 0 &&
  //     !fwdContractsFetching &&
  //     !fwdContractsFetched
  //   ) {
  //     dispatch(fetchForwardContracts({ player_id: player.id }));
  //   }
  // }, [dispatch, player]);

  // Assessment snapshot (gates the Assessment tab). Dispatched only once the
  // retrieve has landed: the createApi saga fills :session_id from
  // active_session, which is 0 until then.
  useEffect(() => {
    if (!session?.id) return;
    dispatch(fetchAssessmentState({}));
  }, [dispatch, player.id, session?.id]);

  if (!session) return <LoadingScreen fullscreen />;

  const isFinished = session.status === FINISHED;

  const isRealTime = session?.scenario_type === REAL_TIME_SCENARIO;

  const sessionHasNoEquities = Object.values(assets).every(
    asset => asset.resourcetype !== 'Equity',
  );

  const showEquitiesResearch = isRealTime && !sessionHasNoEquities;

  console.log(`currentlyPassingTime: ${session.currently_passing_time}`);

  return (
    <div>
      <CookieProvider>
        <TraineeOptionsContextWrapper>
          <TraineeSessionContextWrapper>
            <div>
              <Header />
              <TraineeSimplifiedOptionOrderRefresh />
              <PersonSelector visible={!selectedPerson} />
              <PausedSession
                status={session.status}
                delay={session.delay_minutes_left}
              />
              {session.scenario_type === HISTORICAL_SCENARIO &&
                !player.busted && (
                  <PassingTimeBanner
                    status={session.currently_passing_time}
                    // delay={session.delay_minutes_left}
                  />
                )}
              {/*{player.busted === true && (*/}
              {player.busted === true && !bustBypass && (
                <BustBanner
                  busted={player.busted}
                  // delay={session.delay_minutes_left}
                />
              )}
              <HistoricalProgressTrainee isHistorical={!isRealTime} />
              <div style={{ padding: '30px 70px 70px 70px' }}>
                <Switch>
                  <Route path={`${url}/markets`}>
                    <MarketsTab />
                  </Route>

                  {showEquitiesResearch && (
                    <Route path={`${url}/equities-research`}>
                      <EquitiesResearch />
                    </Route>
                  )}

                  <Route path={`${url}/blotter`}>
                    <BlotterTabRoute whichMarkets={session.which_markets} />
                  </Route>
                  <Route path={`${url}/open-orders`}>
                    <OpenOrders />
                  </Route>
                  <Route path={`${url}/options`}>
                    <OptionsIndex />
                  </Route>
                  <Route path={`${url}/central_bank`}>
                    {/*<Auctions />*/}
                    <CentralBankPlayerTab />
                  </Route>
                  <Route path={`${url}/portfolio`}>
                    <PortfolioTab />
                  </Route>
                  <Route path={`${url}/news2`}>
                    {/*<NewsHistory />*/}
                    <NewsHistory2 />
                  </Route>
                  <Route path={`${url}/assessment`}>
                    <Assessment />
                  </Route>
                  {/*<Route path={`${url}/news`}>*/}
                  {/*  <NewsHistory />*/}
                  {/*  /!*<NewsHistory2 />*!/*/}
                  {/*</Route>*/}
                  {session.allow_individual_benchmark && (
                    <Route path={`${url}/benchmarks`}>
                      <Benchmarks />
                    </Route>
                  )}

                  <Route path={`${url}/metrics`}>
                    <Metrics />
                  </Route>
                  <Route path={`${url}/risk-profile`}>
                    <RiskProfile />
                  </Route>
                  <Route path={`${url}/settings`}>
                    <Settings />
                  </Route>

                  {session.scenario_type === HISTORICAL_SCENARIO && (
                    <Route path={`${url}/macro-background`}>
                      <MacroBackground />
                    </Route>
                  )}

                  <Route path={`${url}/individual_report`}>
                    <IndividualReport player={player} />
                  </Route>
                  {session.show_analysis_studio_tab && (
                    <Route path={`${url}/analysis-studio`}>
                      <AnalysisStudioSelf />
                    </Route>
                  )}
                  <Route path={`${url}/insights`}>
                    <Insights />
                  </Route>

                  <Redirect
                    to={`${url}/${isFinished ? 'portfolio' : 'markets'}`}
                  />
                </Switch>
              </div>
              <SoundPlayer />
            </div>
          </TraineeSessionContextWrapper>
        </TraineeOptionsContextWrapper>
      </CookieProvider>
    </div>
  );
};

export default TraineeDashboard;
